import { definePage, type PluginWebManifest } from '@reflex/plugin-api/web';
import { REPLAYS_PAGE_TAB_SLOT } from '@reflex/plugin-replay/shared/types';

const flowsPage = definePage({
  path: '/automations',
  label: 'Automations',
  icon: 'Workflow',
  group: 'main',
  componentPath: '@reflex/plugin-flows/web/FlowsPage',
  maturityStage: 'preview',
  description: 'Automated multi-agent workflows.',
  details:
    'Build automated workflows that orchestrate multiple agents in sequence or parallel, with approval gates, cron scheduling, and memory across runs.',
  features: ['Multi-agent orchestration', 'Approval gates', 'Cron scheduling', 'Cross-run memory'],
});

/** Legacy `/flows...` URLs that forward to their `/automations...` counterpart. */
const ALIAS_PATHS = [
  '/flows',
  '/flows/runs',
  '/flows/runs/history',
  '/flows/new',
  '/flows/:flowId',
  '/flows/:flowId/edit',
  '/flow-runs/:runId',
  '/flow-replays',
  '/flow-replays/:replayId',
] as const;

export const flowsWeb: PluginWebManifest = {
  ...flowsPage.web,
  routes: [
    ...flowsPage.web.routes!,
    // Static `/automations/runs*` must register before `/automations/:flowId`
    // so `runs` is never parsed as a flow id.
    {
      path: '/automations/runs/history',
      componentPath: '@reflex/plugin-flows/web/FlowsPage',
    },
    { path: '/automations/runs', componentPath: '@reflex/plugin-flows/web/FlowsPage' },
    { path: '/automations/new', componentPath: '@reflex/plugin-flows/web/FlowEditorPage' },
    { path: '/automations/:flowId', componentPath: '@reflex/plugin-flows/web/FlowDetailPage' },
    {
      path: '/automations/:flowId/edit',
      componentPath: '@reflex/plugin-flows/web/FlowEditorPage',
    },
    { path: '/automation-runs/:runId', componentPath: '@reflex/plugin-flows/web/FlowRunPage' },
    {
      // The Automation replays *list* consolidated into the unified Replays
      // page owned by `plugin-replay`, where it is now the Automations tab
      // (see `pageTabs` below). Keeping this index route as a redirect
      // component so existing bookmarks and links land on that tab without
      // flickering the chrome. The detail page below stays here: per-run
      // replay playback is this plugin's surface, the list is its index.
      path: '/automation-replays',
      componentPath: '@reflex/plugin-flows/web/FlowReplaysIndexRedirect',
    },
    {
      path: '/automation-replays/:replayId',
      componentPath: '@reflex/plugin-flows/web/FlowReplayDetailPage',
    },
    // Legacy `/flows*` aliases. Canonical URLs use the product name
    // (`/automations*`); these forward so existing bookmarks and links land.
    ...ALIAS_PATHS.map((path) => ({
      path,
      componentPath: '@reflex/plugin-flows/web/AutomationAliasRedirect',
    })),
  ],
  // Automation replays are labs content: saved runs you play back or embed,
  // not automation configuration. They live as a tab on the Replays page
  // owned by `plugin-replay` — contributed here rather than mounted as a
  // sibling page, so flipping between agent and automation replays is a
  // state change instead of a navigation. Disabling this plugin simply
  // drops the tab.
  pageTabs: [
    {
      slot: REPLAYS_PAGE_TAB_SLOT,
      id: 'automations',
      label: 'Automations',
      icon: 'Workflow',
      componentPath: '@reflex/plugin-flows/web/FlowReplaysTabContent',
      order: 10,
    },
  ],
  // The `Replays` nav item registered by `plugin-replay` is the canonical
  // landing for both kinds; the command below stays a direct deep link into
  // the Automations tab so the palette can jump straight to it.
  navItems: [...(flowsPage.web.navItems ?? [])],
  commands: [
    ...(flowsPage.web.commands ?? []),
    {
      label: 'Automation replays',
      to: '/replays?kind=automations',
      icon: 'Play',
      group: 'misc',
    },
  ],
  resourcePreviews: [
    {
      kind: 'flows',
      label: 'Automation',
      icon: 'Workflow',
      componentPath: '@reflex/plugin-flows/web/FlowPreview',
      detailHref: '/automations/:id',
      // The flow prefix only (`shared/ids.ts` declares many kinds); a bare
      // `flw_…` id anywhere in the app resolves this preview's icon and
      // hover card. Versions/nodes/etc. have no preview to point at.
      idPrefixes: ['flw'],
    },
  ],
  marketingPreviews: [
    {
      id: 'flows-page',
      label: 'Automations page',
      description:
        'Live preview of the automations overview with sample automations and recent runs.',
      surface: 'page',
      componentPath: '@reflex/plugin-flows/web/FlowsPagePreview',
    },
  ],
};
